export const CLASSIC_METRICS_DEFAULTS = {
  rougeEnabled: true,
  bleuEnabled: true,
  meteorEnabled: true,
  bertscoreEnabled: true,
  lengthRatioEnabled: true,
  exactMatchEnabled: true,
  minHashEnabled: true,
  dictMatchEnabled: true,
  // Advanced options
  includeRougeL: true,
  includeRougeW: false,
  bertscoreUseIdf: false,
};

export function mergeClassicMetricsConfig(classicConfig) {
  return { ...CLASSIC_METRICS_DEFAULTS, ...(classicConfig || {}) };
}

/**
 * Map a sample-row or overview metric key onto the classic-metrics toggle
 * that controls it. Overview keys are lowercase (`text_match`); Samples
 * keys are mixed-case (`TEXT_MATCH`, `CompressionRatio`, `DICT_MATCH.foo`).
 * Returns null for unknown keys so callers can keep those columns visible.
 */
export function classicMetricSwitchKey(metricKey) {
  if (metricKey == null) return null;
  const raw = String(metricKey);
  const lower = raw.toLowerCase();
  if (lower.startsWith('dict_match')) return 'dictMatchEnabled';

  const normalized = lower.replace(/-/g, '_');
  if (normalized === 'rouge_1' || normalized === 'rouge_2' || normalized === 'rouge_l') {
    return 'rougeEnabled';
  }
  if (normalized === 'bleu') return 'bleuEnabled';
  if (normalized === 'meteor') return 'meteorEnabled';
  if (normalized === 'bertscore') return 'bertscoreEnabled';
  if (normalized === 'compression_ratio' || normalized === 'compressionratio') {
    return 'lengthRatioEnabled';
  }
  if (normalized === 'text_match') return 'exactMatchEnabled';
  if (normalized === 'min_hash') return 'minHashEnabled';
  return null;
}

/**
 * Whether a classic metric column/row should appear given Advanced / Overview
 * toggles. Unknown keys stay visible so server-specific metrics are not dropped.
 */
export function isClassicMetricVisible(metricKey, classicConfig) {
  const cfg = mergeClassicMetricsConfig(classicConfig);
  const switchKey = classicMetricSwitchKey(metricKey);
  if (!switchKey) return true;
  if (cfg[switchKey] === false) return false;
  const normalized = String(metricKey).toLowerCase().replace(/-/g, '_');
  if (normalized === 'rouge_l' && cfg.includeRougeL === false) return false;
  return true;
}

export const CLASSIC_METRICS_INFO = [
  {
    key: 'rouge',
    title: 'ROUGE',
    subtitle: 'Recall-Oriented Understudy for Gisting Evaluation',
    switchKey: 'rougeEnabled',
  },
  {
    key: 'bleu',
    title: 'BLEU',
    subtitle: 'Bilingual Evaluation Understudy',
    switchKey: 'bleuEnabled',
  },
  {
    key: 'meteor',
    title: 'METEOR',
    subtitle: 'Metric for Evaluation of Translation with Explicit ORdering',
    switchKey: 'meteorEnabled',
  },
  {
    key: 'bertscore',
    title: 'BERTScore',
    subtitle: 'Contextual embedding-based metric',
    switchKey: 'bertscoreEnabled',
  },
  {
    key: 'length_ratio',
    title: 'Length Ratio',
    subtitle: 'Compression ratio and length statistics',
    switchKey: 'lengthRatioEnabled',
  },
  {
    key: 'text_match',
    title: 'Exact Match',
    subtitle: 'Binary exact-string match against the ground truth',
    switchKey: 'exactMatchEnabled',
    contentGenerationOnly: true,
  },
  {
    key: 'min_hash',
    title: 'Fuzzy Similarity (MinHash)',
    subtitle: 'Shingle-based Jaccard similarity against the ground truth',
    switchKey: 'minHashEnabled',
    contentGenerationOnly: true,
  },
  {
    key: 'dict_match',
    title: 'Dict Match',
    subtitle: 'Per-field match for structured (JSON) outputs',
    switchKey: 'dictMatchEnabled',
    contentGenerationOnly: true,
  },
];

/**
 * Metric group for a Configure surface. `'accuracy'` is the content-generation
 * accuracy group (Exact Match, Fuzzy Similarity (MinHash), Dict Match) shown
 * in the "Accuracy Metrics" section/card for content generation tasks only;
 * `'classic'` is every other classic metric ("Classic Metrics"). Both groups
 * persist their toggles into the same `classic_metrics_config` usecase field.
 */
export function getMetricsInfoForScope(scope) {
  const accuracyScope = scope === 'accuracy';
  return CLASSIC_METRICS_INFO.filter((m) => Boolean(m.contentGenerationOnly) === accuracyScope);
}
